/* eslint-disable */
import { useState } from 'react';
import { Task, ExternalTask, TeamAction } from '../types';
import { isDesktop } from '../platform';

export const useStandUpUI = (track: (eventName: string, properties?: any) => void) => {
    const [viewMode, setViewMode] = useState<'full' | 'compact'>('full');
    const [view, setView] = useState<'manager' | 'engineer' | 'summary'>('engineer');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isTeamModalOpen, setIsTeamModalOpen] = useState(false);
    const [isMembersModalOpen, setIsMembersModalOpen] = useState(false);
    const [isTeamSettingsModalOpen, setIsTeamSettingsModalOpen] = useState(false);
    const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
    const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
    const [isMemberDetailOpen, setIsMemberDetailOpen] = useState(false); // NEW
    const [isForceUpdateOpen, setIsForceUpdateOpen] = useState(false);
    const [viewingTeamMembersId, setViewingTeamMembersId] = useState<string | null>(null);
    const [viewingTeamSettingsId, setViewingTeamSettingsId] = useState<string | null>(null);
    const [viewingDetailTask, setViewingDetailTask] = useState<Task | ExternalTask | null>(null);
    const [viewingMemberId, setViewingMemberId] = useState<string | null>(null); // NEW
    const [orderedMemberIds, setOrderedMemberIds] = useState<string[]>([]); // NEW for navigation
    const [editingTask, setEditingTask] = useState<Task | null>(null);
    const [teamAction, setTeamAction] = useState<TeamAction>({ type: 'create' });
    const [toast, setToast] = useState<{message: string, type: 'success' | 'error' | 'info'} | null>(null);

    // Summary View Persistence
    const [summaryDateRange, setSummaryDateRange] = useState<'1d' | '7d' | '30d' | 'all' | 'custom'>('7d');
    const [summaryStartDate, setSummaryStartDate] = useState<Date | null>(new Date(Date.now() - 7 * 24 * 60 * 60 * 1000));
    const [summaryEndDate, setSummaryEndDate] = useState<Date | null>(new Date());
    const [summaryAppliedRange, setSummaryAppliedRange] = useState({
        start: summaryStartDate?.getTime() || 0,
        end: summaryEndDate?.getTime() || 0,
        version: 0
    });

    // Update Global State
    const [updateStatus, setUpdateStatus] = useState<'idle' | 'checking' | 'available' | 'downloading' | 'ready' | 'uptodate' | 'error'>('idle');
    const [updateError, setUpdateError] = useState<string | null>(null);
    const [availableUpdate, setAvailableUpdate] = useState<any>(null);

    const toggleViewMode = async () => {
        const newMode = viewMode === 'full' ? 'compact' : 'full';
        setViewMode(newMode);
        track('view_mode_toggled', { mode: newMode });

        if (isDesktop()) {
            try {
                // High-End Window Management via Platform
                // @ts-ignore
                const { LogicalSize, getCurrentWindow } = await import(/* @vite-ignore */ /* webpackIgnore: true */ '@tauri-apps/api/window');
                const win = getCurrentWindow();

                // 1. SAFETY: Exit Full Screen before moving to compact
                const isFullScreen = await win.isFullscreen();
                if (isFullScreen) {
                    await win.setFullscreen(false);
                }

                if (newMode === 'compact') {
                    await win.setDecorations(false);
                    await win.setShadow(false);
                    await win.setMinSize(new LogicalSize(300, 300));
                    await win.setSize(new LogicalSize(360, 400));
                    await win.setAlwaysOnTop(true);
                } else {
                    await win.setDecorations(true);
                    await win.setShadow(true);
                    await win.setMinSize(new LogicalSize(800, 600));
                    await win.setSize(new LogicalSize(1000, 800));
                    await win.setAlwaysOnTop(false);
                    await win.center();
                }
            } catch (e) {
                console.error("Failed to resize window", e);
            }
        }
    };

    const openModal = (task?: Task | ExternalTask) => {
        if (task && (task as ExternalTask).externalSource) {
            // External tasks are read-only for now in the main modal
            return;
        }
        track('opened_task_create_modal', { isEdit: !!task });
        setEditingTask(task as Task || null);
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setEditingTask(null);
    };

    const openTeamModal = (action: TeamAction) => {
        track('opened_team_modal', { action: action.type });
        setTeamAction(action);
        setIsTeamModalOpen(true);
    };

    const closeTeamModal = () => setIsTeamModalOpen(false);

    const openMembersModal = (teamId: string) => {
        track('opened_team_members_modal', { teamId });
        setViewingTeamMembersId(teamId);
        setIsMembersModalOpen(true);
    };

    const closeMembersModal = () => {
        setIsMembersModalOpen(false);
        setViewingTeamMembersId(null);
    };

    const openTeamSettingsModal = (teamId: string) => {
        track('opened_team_settings_modal', { teamId });
        setViewingTeamSettingsId(teamId);
        setIsTeamSettingsModalOpen(true);
    };

    const closeTeamSettingsModal = () => {
        setIsTeamSettingsModalOpen(false);
        setViewingTeamSettingsId(null);
    };

    const openSettingsModal = () => {
        track('opened_app_settings_modal');
        setIsSettingsModalOpen(true);
    };
    const closeSettingsModal = () => setIsSettingsModalOpen(false);

    const openDetailModal = (task: Task | ExternalTask) => {
        const isExternal = !!(task as ExternalTask).externalSource;
        track('opened_task_detail_modal', { 
            taskId: task.id, 
            isExternal,
            provider: isExternal ? (task as ExternalTask).externalSource : 'native'
        });
        setViewingDetailTask(task);
        setIsDetailModalOpen(true);
    };

    const closeDetailModal = () => {
        setIsDetailModalOpen(false);
        setViewingDetailTask(null);
    };

    const openMemberDetail = (userId: string, orderedIds?: string[]) => {
        track('opened_member_detail_modal', { userId });
        setViewingMemberId(userId);
        if (orderedIds) setOrderedMemberIds(orderedIds);
        setIsMemberDetailOpen(true);
    };

    const closeMemberDetail = () => {
        setIsMemberDetailOpen(false);
        setViewingMemberId(null);
        setOrderedMemberIds([]);
    };

    const openForceUpdateModal = () => setIsForceUpdateOpen(true);
    const closeForceUpdateModal = () => setIsForceUpdateOpen(false);

    const showToast = (message: string, type: 'success' | 'error' | 'info' = 'success') => {
        setToast({ message, type });
        setTimeout(() => setToast(null), 3000);
    };

    return {
        viewMode,
        setViewMode,
        toggleViewMode,
        view,
        setView,
        isModalOpen,
        isTeamModalOpen,
        isMembersModalOpen,
        isTeamSettingsModalOpen,
        isSettingsModalOpen,
        isDetailModalOpen,
        isMemberDetailOpen,
        isForceUpdateOpen,
        viewingTeamMembersId,
        viewingTeamSettingsId,
        viewingDetailTask,
        viewingMemberId,
        orderedMemberIds,
        editingTask,
        teamAction,
        toast,
        updateStatus, setUpdateStatus,
        updateError, setUpdateError,
        availableUpdate, setAvailableUpdate,
        openModal,
        closeModal,
        openTeamModal,
        closeTeamModal,
        openMembersModal,
        closeMembersModal,
        openTeamSettingsModal,
        closeTeamSettingsModal,
        openForceUpdateModal,
        closeForceUpdateModal,
        openSettingsModal,
        closeSettingsModal,
        openDetailModal,
        closeDetailModal,
        openMemberDetail,
        closeMemberDetail,
        summaryDateRange,
        setSummaryDateRange,
        summaryStartDate,
        setSummaryStartDate,
        summaryEndDate,
        setSummaryEndDate,
        summaryAppliedRange,
        setSummaryAppliedRange,
        showToast
    };
};
